import React, { useState, useEffect } from 'react';
import api from '../api/api';
import './SliderManagement.css';
import { formatDate } from '../utils/dashboardTranslations';

const SliderManagement = () => {
  const [sliderImages, setSliderImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [newImage, setNewImage] = useState({
    file: null,
    order: 0
  });

  useEffect(() => {
    fetchSliderImages();
  }, []);

  const fetchSliderImages = async () => {
    try {
      const response = await api.get('/Slider/admin');
      setSliderImages(response.data);
    } catch (error) {
      console.error('Error fetching slider images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();
    if (!newImage.file) {
      alert('يرجى اختيار ملف صورة');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', newImage.file);
      formData.append('title', 'Slider Image');
      formData.append('description', 'Image for slider');
      formData.append('order', newImage.order.toString());

      // Debug logging
      console.log('Uploading image with data:');
      console.log('File:', newImage.file?.name, 'Size:', newImage.file?.size, 'Type:', newImage.file?.type);
      console.log('Order:', newImage.order);
      console.log('FormData entries:');
      for (let [key, value] of formData.entries()) {
        console.log(key, ':', value);
      }

      const response = await api.post('/Slider', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      await fetchSliderImages();
      setNewImage({ file: null, order: 0 });
      document.getElementById('imageFile').value = '';
      alert('تم رفع الصورة بنجاح');
    } catch (error) {
      console.error('Error uploading image:', error);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);
      console.error('Validation errors:', JSON.stringify(error.response?.data?.errors, null, 2));
      alert(`فشل رفع الصورة: ${error.response?.data?.title || error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleEditImage = async (e) => {
    e.preventDefault();
    if (!editingImage) return;

    try {
      // If user selected a new file, we need to delete the old one and create a new one
      if (editingImage.file) {
        // Delete the old image
        await api.delete(`/Slider/${editingImage.id}`);
        
        // Upload the new image with the same order
        const formData = new FormData();
        formData.append('image', editingImage.file);
        formData.append('title', 'Slider Image');
        formData.append('description', 'Image for slider');
        formData.append('order', editingImage.order.toString());

        await api.post('/Slider', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        // Just update the metadata (order only, since we don't use title/description)
        const updateData = {
          title: 'Slider Image',
          description: 'Image for slider',
          order: editingImage.order,
          isActive: editingImage.isActive
        };

        await api.put(`/Slider/${editingImage.id}`, updateData, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
      }

      await fetchSliderImages();
      setEditingImage(null);
      alert('تم تحديث الصورة بنجاح');
    } catch (error) {
      console.error('Error updating image:', error);
      alert('فشل تحديث الصورة');
    }
  };

  const handleDeleteImage = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الصورة؟')) return;

    try {
      await api.delete(`/Slider/${id}`);
      await fetchSliderImages();
      alert('تم حذف الصورة بنجاح');
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('فشل حذف الصورة');
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await api.put(`/Slider/${id}/toggle`);
      await fetchSliderImages();
    } catch (error) {
      console.error('Error toggling image status:', error);
      alert('فشل تحديث حالة الصورة');
    }
  };

  // Helper to get full image URL
  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url.replace(':5000', '');
    // Use the API base URL (without /api) for all relative paths
    const apiBase = (process.env.REACT_APP_API_URL || '').replace('/api', '');
    return apiBase + url;
  };

  if (loading) {
    return <div className="loading">جاري تحميل صور السلايدر...</div>;
  }

  return (
    <div className="slider-management">
      <h2>إدارة صور السلايدر</h2>

      {/* Upload New Image Form */}
      <div className="upload-section">
        <h3>رفع صورة جديدة للسلايدر</h3>
        <form onSubmit={handleImageUpload} className="upload-form">
          <div className="form-group">
            <label htmlFor="imageFile">ملف الصورة:</label>
            <input
              type="file"
              id="imageFile"
              accept="image/*"
              onChange={(e) => setNewImage({ ...newImage, file: e.target.files[0] })}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="order">ترتيب العرض:</label>
            <input
              type="number"
              id="order"
              value={newImage.order}
              onChange={(e) => setNewImage({ ...newImage, order: parseInt(e.target.value) || 0 })}
              min="0"
            />
          </div>
          <button type="submit" disabled={uploading} className="btn btn-primary">
            {uploading ? 'جارٍ الرفع...' : 'رفع الصورة'}
          </button>
        </form>
      </div>

      {/* Images List */}
      <div className="images-list">
        <h3>صور السلايدر الحالية</h3>
        {sliderImages.length === 0 ? (
          <p>لا توجد صور مضافة للسلايدر.</p>
        ) : (
          <div className="images-grid">
            {sliderImages.map((image) => (
              <div key={image.id} className={`image-card ${!image.isActive ? 'inactive' : ''}`}>
                <img 
                  src={getImageUrl(image.imageUrl)} 
                  alt={`Slider image ${image.order}`}
                  className="slider-preview"
                />
                <div className="image-info">
                  <h4>صورة السلايدر {image.order}</h4>
                  <p><strong>الترتيب:</strong> {image.order}</p>
                  <p><strong>الحالة:</strong> {image.isActive ? 'فعّالة' : 'معطّلة'}</p>
                  <p><strong>تاريخ الإنشاء:</strong> {formatDate(image.createdAt)}</p>
                </div>
                <div className="image-actions">
                  <button
                    onClick={() => setEditingImage({ ...image, file: null, isActive: image.isActive })}
                    className="btn btn-edit"
                  >
                    تعديل
                  </button>
                  <button
                    onClick={() => handleToggleActive(image.id, image.isActive)}
                    className={`btn ${image.isActive ? 'btn-deactivate' : 'btn-activate'}`}
                  >
                    {image.isActive ? 'تعطيل' : 'تفعيل'}
                  </button>
                  <button
                    onClick={() => handleDeleteImage(image.id)}
                    className="btn btn-delete"
                  >
                    حذف
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingImage && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>تعديل صورة السلايدر</h3>
            <form onSubmit={handleEditImage} className="edit-form">
              <div className="form-group">
                <label htmlFor="editImageFile">ملف صورة جديد (اختياري):</label>
                <input
                  type="file"
                  id="editImageFile"
                  accept="image/*"
                  onChange={(e) => setEditingImage({ ...editingImage, file: e.target.files[0] })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="editOrder">ترتيب العرض:</label>
                <input
                  type="number"
                  id="editOrder"
                  value={editingImage.order}
                  onChange={(e) => setEditingImage({ ...editingImage, order: parseInt(e.target.value) || 0 })}
                  min="0"
                />
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn btn-primary">
                  تحديث الصورة
                </button>
                <button 
                  type="button" 
                  onClick={() => setEditingImage(null)}
                  className="btn btn-secondary"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SliderManagement;
